import React, {
  memo, useCallback, useEffect, useState,
} from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import localization from '../../shared/strings';
import Tooltip from '../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import normalizeHandle from './helpers/normalizeHandle';
import { useLocalFieldValue, useEnterToBlur } from './hooks';
import { productFieldInputProps } from './constants';
import styles from './styles.module.scss';

const l10n = localization.PRODUCT_INFO.seo;

const ProductHandle = ({
  value, readOnly, readOnlyTooltip, tooltip, onChange, isDiff,
}) => {
  const { localValue, setLocalValue } = useLocalFieldValue(value);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLocalValue(value);
    setError(null);
  }, [value, setLocalValue]);

  const handleChange = useCallback((e) => {
    if (readOnly) return;
    const nextValue = e.target.value;
    setLocalValue(nextValue);
    if (error) {
      const trimmed = nextValue.trim();
      if (!trimmed || normalizeHandle(trimmed)) setError(null);
    }
  }, [error, readOnly, setLocalValue]);

  const handleBlur = useCallback(() => {
    if (readOnly) return;

    const trimmed = localValue.trim();
    if (trimmed !== localValue) setLocalValue(trimmed);

    // Intentional clear is allowed.
    if (!trimmed) {
      setError(null);
      if (trimmed !== value) onChange?.(trimmed);
      return;
    }

    const normalized = normalizeHandle(trimmed);
    if (!normalized) {
      setError(l10n.handleNormalizesToEmpty);
      setLocalValue(value);
      return;
    }

    setError(null);
    if (normalized !== localValue) setLocalValue(normalized);
    if (normalized !== value) onChange?.(normalized);
  }, [localValue, onChange, readOnly, setLocalValue, value]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ReadOnlyFieldTooltip tooltip={tooltip || (readOnly ? readOnlyTooltip : null)}>
      <div className={styles.row}>
        <TextField
          value={localValue}
          disabled={readOnly}
          label={(
            <Tooltip content={isDiff ? 'Unpublished changes' : null} placement="top">
              <span className={isDiff ? styles.diffDot : undefined}>{l10n.handle}</span>
            </Tooltip>
          )}
          error={Boolean(error)}
          helperText={error}
          inputProps={productFieldInputProps}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
      </div>
    </ReadOnlyFieldTooltip>
  );
};

ProductHandle.propTypes = {
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  tooltip: PropTypes.string,
  isDiff: PropTypes.bool,
  onChange: PropTypes.func,
};

ProductHandle.defaultProps = {
  value: '',
  readOnly: false,
  readOnlyTooltip: null,
  tooltip: null,
  isDiff: false,
  onChange: null,
};

export default memo(ProductHandle);
